/**
 * Loadout Section — shared render module for Weapons & Equipment Phase 4
 * (#223, SPEC_weapons_equipment §3/§5/§6).
 *
 * Displays a runner's equipped loadout (weapon + armor slot + combat
 * modifier) and — when owner callbacks are supplied — the owner-only Armory
 * and Consumable store plus the buy / equip / unequip / manufacture flows.
 * The UI only EMITS actions (buy_weapon / equip / unequip / manufacture);
 * the server is authoritative on tier gating, affordability and possession.
 *
 * Mirrors the cyberware-section.ts split: `renderLoadoutUI` emits HTML,
 * `wireLoadoutUI` attaches handlers, `injectLoadoutStyles` injects CSS once.
 * Display-only mode (no callbacks) is used by the playback / rival view —
 * only the equipped-slot read-out renders, no stores or controls.
 */

import type { RunnerDTO, ArmoryItemDTO, ConsumableItemDTO } from '../data/fixture';
import { t } from '../i18n';

// ── Public API ───────────────────────────────────────────────────────────

export interface LoadoutUIOptions {
  runner: RunnerDTO;
  /** Owner-only Armory stacks (unequipped weapons/armor). */
  armory?: ArmoryItemDTO[];
  /** Owner-only single-use combat items. */
  consumables?: ConsumableItemDTO[];
  /** Capacities for the store headers. */
  armoryCapacity?: number;
  consumableCapacity?: number;
  /** Owner's dirty cash — drives the buy affordability gate (server still
   *  validates on submit). Undefined leaves affordability unchecked. */
  dirtyCash?: number;
  /** BUY_WEAPON handler. Undefined → display-only (no stores/controls). */
  onBuy?: (runnerId: string, itemType: string) => Promise<boolean>;
  /** EQUIP handler (Armory → slot). */
  onEquip?: (runnerId: string, itemType: string) => Promise<boolean>;
  /** UNEQUIP handler (slot → Armory). slot = "weapon" | "armor". */
  onUnequip?: (runnerId: string, slot: 'weapon' | 'armor') => Promise<boolean>;
  /** MANUFACTURE handler (item → store). itemType = weapon_melee | incendiary. */
  onManufacture?: (runnerId: string, itemType: string) => Promise<boolean>;
}

type ItemKind = 'weapon' | 'armor' | 'consumable';

interface CatalogItem {
  itemType: string;
  kind: ItemKind;
  tier: number;
  dirtyPrice: number;
}

/** Buyable item catalog — mirrors corpllm-server data/weapons.json. The client
 *  needs prices/tiers to render the buy picker; the server re-validates tier
 *  (maxWeaponTier) + affordability on submit, so this is a display convenience,
 *  not the source of truth. Keep in sync with weapons.json on price/tier edits. */
const CATALOG: CatalogItem[] = [
  { itemType: 'weapon_melee', kind: 'weapon', tier: 1, dirtyPrice: 1500 },
  { itemType: 'weapon_light', kind: 'weapon', tier: 2, dirtyPrice: 5000 },
  { itemType: 'weapon_ranged', kind: 'weapon', tier: 3, dirtyPrice: 12000 },
  { itemType: 'weapon_heavy', kind: 'weapon', tier: 4, dirtyPrice: 25000 },
  { itemType: 'armor_basic', kind: 'armor', tier: 2, dirtyPrice: 4000 },
  { itemType: 'armor_tactical', kind: 'armor', tier: 3, dirtyPrice: 10000 },
  { itemType: 'armor_military', kind: 'armor', tier: 4, dirtyPrice: 22000 },
  { itemType: 'incendiary', kind: 'consumable', tier: 2, dirtyPrice: 3000 },
  { itemType: 'explosive', kind: 'consumable', tier: 3, dirtyPrice: 8000 },
  // 'drone' (tier 4) is intentionally NOT offered in the buy picker: its only
  // use, DRONE_STRIKE, is enabled:false on the server (engagement loop unbuilt).
  // The server plumbing + catalog entry exist; the picker excludes it until the
  // action ships so players can't buy a $18k item they can't use.
];

const KNOWN_ITEM_TYPES = new Set([
  ...CATALOG.map(c => c.itemType),
  'drone', // still display its name if somehow held
]);

/** Emit the section HTML. Always returns the equipped-slot read-out; the
 *  owner stores + controls are appended only when a buy callback is supplied. */
export function renderLoadoutUI(opts: LoadoutUIOptions): string {
  return `
    <div class="lw-section" id="runner-detail-loadout">
      <div class="lw-section-title">${escapeHTML(t('loadout.title'))}</div>
      ${renderSlots(opts)}
      ${renderOwnerArea(opts)}
    </div>
  `;
}

/** Wire interactive controls inside `root`. Idempotent per render. */
export function wireLoadoutUI(root: HTMLElement, opts: LoadoutUIOptions): void {
  const runnerId = opts.runner.id;

  // Generic async-button runner: disables, swaps label, calls the handler,
  // surfaces an inline error on failure. On success the caller's fixture
  // refresh re-renders the panel, so we leave the button disabled.
  const runAction = async (
    btn: HTMLButtonElement,
    handler: () => Promise<boolean>,
  ): Promise<void> => {
    const err = root.querySelector<HTMLDivElement>('#lw-error');
    const original = btn.textContent ?? '';
    btn.disabled = true;
    btn.textContent = t('loadout.submitting');
    if (err) err.style.display = 'none';
    try {
      const ok = await handler();
      if (!ok && err) {
        err.textContent = t('loadout.error');
        err.style.display = 'block';
        btn.disabled = false;
        btn.textContent = original;
      }
    } catch (e) {
      if (err) {
        err.textContent = e instanceof Error ? e.message : t('loadout.error');
        err.style.display = 'block';
      }
      btn.disabled = false;
      btn.textContent = original;
    }
  };

  // UNEQUIP buttons on equipped slots.
  if (opts.onUnequip) {
    root.querySelectorAll<HTMLButtonElement>('[data-lw-unequip]').forEach(btn => {
      btn.addEventListener('click', () => {
        const slot = btn.dataset.lwUnequip as 'weapon' | 'armor';
        void runAction(btn, () => opts.onUnequip!(runnerId, slot));
      });
    });
  }

  // EQUIP buttons on armory rows.
  if (opts.onEquip) {
    root.querySelectorAll<HTMLButtonElement>('[data-lw-equip]').forEach(btn => {
      btn.addEventListener('click', () => {
        const itemType = btn.dataset.lwEquip!;
        void runAction(btn, () => opts.onEquip!(runnerId, itemType));
      });
    });
  }

  // MANUFACTURE buttons.
  if (opts.onManufacture) {
    root.querySelectorAll<HTMLButtonElement>('[data-lw-manufacture]').forEach(btn => {
      btn.addEventListener('click', () => {
        const itemType = btn.dataset.lwManufacture!;
        void runAction(btn, () => opts.onManufacture!(runnerId, itemType));
      });
    });
  }

  // BUY form: item picker → price/tier/affordability preview → submit.
  if (opts.onBuy) {
    const sel = root.querySelector<HTMLSelectElement>('#lw-buy-select');
    const submit = root.querySelector<HTMLButtonElement>('#lw-buy-submit');
    const preview = root.querySelector<HTMLDivElement>('#lw-buy-preview');
    if (sel && submit && preview) {
      const refresh = (): void => {
        const item = CATALOG.find(c => c.itemType === sel.value);
        if (!item) {
          preview.innerHTML = '';
          submit.disabled = true;
          return;
        }
        const affordable = opts.dirtyCash == null || opts.dirtyCash >= item.dirtyPrice;
        const costCls = affordable ? '' : 'lw-bad';
        preview.innerHTML = `
          <span class="${costCls}">${escapeHTML(t('loadout.cost_dirty'))} $${item.dirtyPrice.toLocaleString()}</span>
          <span class="lw-tier">${escapeHTML(tierHint(item.tier))}</span>
          ${affordable ? '' : `<span class="lw-bad">${escapeHTML(t('loadout.unaffordable'))}</span>`}
        `;
        submit.disabled = !affordable;
      };
      sel.addEventListener('change', refresh);
      submit.addEventListener('click', () => {
        const item = CATALOG.find(c => c.itemType === sel.value);
        if (!item) return;
        void runAction(submit, () => opts.onBuy!(runnerId, item.itemType));
      });
      refresh();
    }
  }
}

/** Inject the section CSS. Idempotent via the id check. */
export function injectLoadoutStyles(): void {
  if (document.querySelector('#loadout-section-style')) return;
  const s = document.createElement('style');
  s.id = 'loadout-section-style';
  s.textContent = CSS;
  document.head.appendChild(s);
}

// ── Private rendering ──────────────────────────────────────────────────────

function renderSlots(opts: LoadoutUIOptions): string {
  const lo = opts.runner.loadout;
  const weaponName = lo?.weaponType ? itemLabel(lo.weaponType, lo.weaponName) : '';
  const armorName = lo?.armorType ? itemLabel(lo.armorType, lo.armorName) : '';
  const canUnequip = !!opts.onUnequip && opts.runner.status !== 'arrested';

  const weaponMod = lo?.weaponModifier
    ? `<span class="lw-mod lw-mod-dmg">${t('loadout.modifier_dmg', { mod: String(lo.weaponModifier) })}</span>` : '';
  const armorMod = lo?.armorModifier
    ? `<span class="lw-mod lw-mod-armor">${t('loadout.modifier_armor', { mod: String(lo.armorModifier) })}</span>` : '';

  const slot = (
    label: string, itemType: string | undefined, name: string, mod: string, slotKey: 'weapon' | 'armor',
  ): string => {
    const filled = !!itemType;
    const unequipBtn = filled && canUnequip
      ? `<button class="btn red sm" data-lw-unequip="${slotKey}">${escapeHTML(t('loadout.unequip'))}</button>` : '';
    return `
      <div class="lw-slot${filled ? '' : ' lw-slot-empty'}">
        <span class="lw-slot-label">${escapeHTML(label)}</span>
        <span class="lw-slot-item">${filled ? escapeHTML(name) : escapeHTML(t('loadout.empty_slot'))}</span>
        ${mod}
        ${unequipBtn}
      </div>
    `;
  };

  return `
    <div class="lw-slots">
      ${slot(t('loadout.slot_weapon'), lo?.weaponType, weaponName, weaponMod, 'weapon')}
      ${slot(t('loadout.slot_armor'), lo?.armorType, armorName, armorMod, 'armor')}
    </div>
  `;
}

/** Owner-only area: only rendered when a buy callback is supplied (the
 *  active-roster owner path). Stores + buy + manufacture controls. */
function renderOwnerArea(opts: LoadoutUIOptions): string {
  if (!opts.onBuy) return ''; // display-only (rival / playback)
  return renderArmory(opts) + renderConsumables(opts) + renderBuyForm() + renderManufacture(opts)
    + `<div class="lw-error" id="lw-error" style="display:none"></div>`;
}

function renderArmory(opts: LoadoutUIOptions): string {
  const items = opts.armory ?? [];
  const total = items.reduce((s, it) => s + it.count, 0);
  const capLabel = opts.armoryCapacity != null ? ` ${total}/${opts.armoryCapacity}` : '';
  const canEquip = !!opts.onEquip && opts.runner.status !== 'arrested';
  const body = items.length === 0
    ? `<div class="lw-empty">${escapeHTML(t('loadout.armory_empty'))}</div>`
    : items.map(it => `
        <div class="lw-store-row">
          <span class="chip ${it.slot === 'weapon' ? 'info' : 'me'}">${escapeHTML(itemLabel(it.itemType, it.name))}</span>
          <span class="lw-count">×${it.count}</span>
          ${canEquip ? `<button class="btn cyan sm" data-lw-equip="${escapeAttr(it.itemType)}">${escapeHTML(t('loadout.equip'))}</button>` : ''}
        </div>`).join('');
  return `
    <div class="lw-store">
      <div class="lw-store-title">${escapeHTML(t('loadout.armory_title'))}<span class="lw-cap">${escapeHTML(capLabel)}</span></div>
      ${body}
    </div>
  `;
}

function renderConsumables(opts: LoadoutUIOptions): string {
  const items = opts.consumables ?? [];
  const total = items.reduce((s, it) => s + it.count, 0);
  const capLabel = opts.consumableCapacity != null ? ` ${total}/${opts.consumableCapacity}` : '';
  const body = items.length === 0
    ? `<div class="lw-empty">${escapeHTML(t('loadout.consumables_empty'))}</div>`
    : items.map(it => `
        <div class="lw-store-row">
          <span class="chip warn">${escapeHTML(itemLabel(it.itemType, it.name))}</span>
          <span class="lw-count">×${it.count}</span>
        </div>`).join('');
  return `
    <div class="lw-store">
      <div class="lw-store-title">${escapeHTML(t('loadout.consumables_title'))}<span class="lw-cap">${escapeHTML(capLabel)}</span></div>
      ${body}
    </div>
  `;
}

function renderBuyForm(): string {
  const groups: Array<{ kind: ItemKind; labelKey: string }> = [
    { kind: 'weapon', labelKey: 'loadout.group_weapons' },
    { kind: 'armor', labelKey: 'loadout.group_armor' },
    { kind: 'consumable', labelKey: 'loadout.group_consumables' },
  ];
  const optgroups = groups.map(g => {
    const opts2 = CATALOG.filter(c => c.kind === g.kind).map(c =>
      `<option value="${escapeAttr(c.itemType)}">${escapeHTML(itemLabel(c.itemType))} · T${c.tier} · $${c.dirtyPrice.toLocaleString()}</option>`,
    ).join('');
    return `<optgroup label="${escapeAttr(t(g.labelKey))}">${opts2}</optgroup>`;
  }).join('');
  return `
    <div class="lw-buy">
      <div class="lw-store-title">${escapeHTML(t('loadout.buy_title'))}</div>
      <select class="field" id="lw-buy-select" aria-label="${escapeAttr(t('loadout.buy_pick'))}">${optgroups}</select>
      <div class="lw-buy-preview" id="lw-buy-preview"></div>
      <button class="btn amber sm" id="lw-buy-submit">${escapeHTML(t('loadout.buy_action'))}</button>
    </div>
  `;
}

function renderManufacture(opts: LoadoutUIOptions): string {
  if (!opts.onManufacture) return '';
  return `
    <div class="lw-buy">
      <div class="lw-store-title">${escapeHTML(t('loadout.manufacture_title'))}</div>
      <div class="lw-mf-row">
        <button class="btn violet sm" data-lw-manufacture="weapon_melee">${escapeHTML(t('loadout.manufacture_melee'))}</button>
        <button class="btn violet sm" data-lw-manufacture="incendiary">${escapeHTML(t('loadout.manufacture_incendiary'))}</button>
      </div>
      <div class="lw-hint">${escapeHTML(t('loadout.manufacture_hint'))}</div>
    </div>
  `;
}

// ── Module helpers ───────────────────────────────────────────────────────

/** Localize an item_type via the items.* namespace; falls back to the
 *  server-supplied display name (or the raw type) for unknown types. */
function itemLabel(itemType: string, fallbackName?: string): string {
  if (KNOWN_ITEM_TYPES.has(itemType)) return t(`items.${itemType}`);
  return fallbackName || itemType;
}

function tierHint(tier: number): string {
  switch (tier) {
    case 1: return t('loadout.tier_hint_1');
    case 2: return t('loadout.tier_hint_2');
    case 3: return t('loadout.tier_hint_3');
    case 4: return t('loadout.tier_hint_4');
    default: return '';
  }
}

function escapeHTML(s: string): string {
  return s.replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c] || c));
}

function escapeAttr(s: string): string {
  return escapeHTML(s);
}

// ── Styles ───────────────────────────────────────────────────────────────

const CSS = `
  .lw-section {
    background: var(--bg, #0a0e17);
    border: 1px solid var(--border, #1e293b);
    border-radius: 3px;
    padding: 10px;
    margin-top: 8px;
  }
  .lw-section-title {
    font: 700 12px var(--sans, system-ui);
    color: var(--text-hi, #f8fafc);
    margin-bottom: 8px;
  }
  .lw-slots { display: flex; flex-direction: column; gap: 6px; }
  .lw-slot {
    background: var(--bg-card, #111827);
    border: 1px solid var(--border, #1e293b);
    border-left: 3px solid var(--cyan, #00f5d4);
    padding: 6px 8px;
    font: 11px var(--mono, monospace);
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .lw-slot-empty { border-left-color: var(--text-lo, #64748b); opacity: 0.75; }
  .lw-slot-label {
    color: var(--text-lo, #64748b);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-size: 9px;
    min-width: 52px;
  }
  .lw-slot-item { color: var(--text-hi, #f8fafc); font-weight: 600; flex: 1; }
  .lw-mod {
    font-size: 10px;
    padding: 1px 5px;
    border: 1px solid currentColor;
    letter-spacing: 0.05em;
  }
  .lw-mod-dmg { color: var(--amber, #fca311); }
  .lw-mod-armor { color: var(--cyan, #00f5d4); }

  .lw-store, .lw-buy { margin-top: 8px; }
  .lw-store-title {
    font: 9px var(--mono, monospace);
    color: var(--text-lo, #64748b);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    margin-bottom: 4px;
    display: flex;
    justify-content: space-between;
  }
  .lw-cap { color: var(--text-mid, #94a3b8); }
  .lw-store-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 2px 0;
  }
  .lw-count {
    font: 11px var(--mono, monospace);
    color: var(--text-mid, #94a3b8);
    font-variant-numeric: tabular-nums;
    flex: 1;
  }
  .lw-empty {
    color: var(--text-lo, #64748b);
    font: 11px var(--mono, monospace);
    font-style: italic;
    padding: 4px 0;
  }
  .lw-buy select.field {
    width: 100%;
    background: var(--bg-card, #111827);
    border: 1px solid var(--border, #1e293b);
    color: var(--text, #e0e8f0);
    font: 11px var(--mono, monospace);
    padding: 4px 6px;
  }
  .lw-buy select.field:focus {
    outline: 1px solid var(--cyan, #00f5d4);
    outline-offset: 1px;
  }
  .lw-buy-preview {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    font: 10px var(--mono, monospace);
    color: var(--text-lo, #64748b);
    padding: 4px 0;
  }
  .lw-tier { color: var(--text-mid, #94a3b8); }
  .lw-bad { color: var(--red, #e63946); }
  .lw-mf-row { display: flex; gap: 6px; flex-wrap: wrap; }
  .lw-hint {
    font: 10px var(--mono, monospace);
    color: var(--text-lo, #64748b);
    font-style: italic;
    margin-top: 4px;
  }
  .lw-error {
    color: var(--red, #e63946);
    font: 10px var(--mono, monospace);
    padding: 4px 0;
  }
`;
